import { useCallback } from "react";
import { useIntl } from "react-intl";
import {
  GET_VENUES,
  useVenueCreateMutation,
} from "@ticketingplatform/api/dist/venue";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATHS } from "const/paths";
import { ErrorCode } from "@ticketingplatform/api/dist/const/enums";
import usePrompt from "hooks/usePrompt";
import getApolloNonFieldErrors from "utils/getApolloNonFieldErrors";
import useApolloErrorFields from "hooks/useApolloErrorFields";

import { VenueFormProps, VenueFormValues } from "../VenueForm/types";

export const useVenueAddForm = (): VenueFormProps => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [createVenue, { loading, error }] = useVenueCreateMutation({
    evictQueries: [GET_VENUES],
  });
  const fields = useApolloErrorFields(error);

  usePrompt(loading);

  const onFinish = useCallback(
    async ({
      input: {
        organizationId: organization,
        address: { country, ...address },
        ...input
      },
    }: VenueFormValues) => {
      try {
        await createVenue({
          variables: {
            input: {
              ...input,
              organizationId: organization?.id,
              address: {
                ...address,
                countryCode: country?.value,
                country: country?.label,
              },
            },
          },
        });

        message.success(
          intl.formatMessage({
            defaultMessage: "Venue has been added!",
          })
        );

        navigate(ROUTE_PATHS.VenuesList, {
          state: {
            skipBlock: true,
          },
        });
      } catch (err) {
        const nonFieldError = getApolloNonFieldErrors(err).find(
          ({ errorType }) => errorType && errorType !== ErrorCode.INTERNAL_ERROR
        )?.message;

        message.error(
          nonFieldError ||
            intl.formatMessage(
              { defaultMessage: 'Failed to add Venue "{name}".' },
              { name: input.name }
            )
        );
      }
    },
    [createVenue, intl, navigate]
  );

  return {
    loading,
    onFinish,
    fields,
  };
};
